import React from 'react'
import { fromJS, List } from 'immutable'
import { useQuery } from '@apollo/client'
import _compact from 'lodash/compact'
import _get from 'lodash/get'
import ImmutablePropTypes from 'react-immutable-proptypes'
import {
  GET_CONTENT,
} from '../../services/content/query'
import {
  CONTENT_IMAGE_TYPE_POSTER_3X4,
  createTitleGroupId,
  getOrderedContent,
} from '../../services/content/index'
import { MAX_ROW_ITEMS } from '../../services/pm-section/index'
import { H2 } from '../Heading/index'
import Button, { TYPES as BUTTON_TYPES } from '../Button/index'
import { APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST } from '../../services/apollo-graphql/index'
import SharePageSlider from './SharePageSlider'
import SharePageExperts from './SharePageExperts'

export const BACKGROUND_STYLES = {
  CURVED_GRADIENT: 'curved-gradient',
  CURVED_WHITE: 'curved-white',
}

/**
 * Media showcase section with title, slider, experts, and CTA.
 *
 * @param {Map} sectionData - Section content and config
 * @param {string} language - User/page language
 * @param {boolean} autoplay - Slider autoplay flag
 * @param {string} backgroundStyle - Optional background style
 * @param {string} ctaLabel - CTA button label
 * @param {Object} offerLink - Offer link props
 * @param {function} onOfferClick - CTA click handler
 * @returns {JSX.Element|null}
 */
function SharePageMediaShowcase (props) {
  const {
    sectionData,
    language,
    autoplay,
    backgroundStyle,
    ctaLabel,
    offerLink,
    onOfferClick,
    pmSection,
  } = props
  // sectionData is the union type data (app.data from indexByAdminTitle)
  // So we access header, footer, tabs directly (not through 'data' key)
  // Find sectionId by matching the data in pmSection
  const sectionId = pmSection ? pmSection.findKey((section) => {
    const sectionAppData = section.getIn(['data', 'data'])
    return sectionAppData && sectionAppData.equals(sectionData)
  }) : undefined
  const title = sectionData.getIn(['header', 'label'])
  const text = sectionData.getIn(['footer', 'label'])
  const items = sectionData.getIn(['tabs', 0, 'tabContent'])
  const hasPeople = !!sectionData.getIn(['tabs', 1])
  const itemsGroupIds = _compact(
    items.map((item) => createTitleGroupId(item.get('contentId'), item.get('contentType'))).toJS(),
  )

  const backgroundClass = backgroundStyle ? ` share-page__media-showcase--${backgroundStyle}` : ''

  if (!itemsGroupIds.length) return null

  const { data, loading } = useQuery(GET_CONTENT, {
    variables: {
      language: [language, '_primary'],
      primaryLanguage: [language],
      titleGroupIds: itemsGroupIds,
      imageType: [CONTENT_IMAGE_TYPE_POSTER_3X4],
      limitedData: true,
      includeSeriesImage: false,
      learnerLevelLanguage: language,
    },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  })

  const itemsContent = _get(data, ['contentList', 'content'], [])
  const itemsOrdered = getOrderedContent(itemsGroupIds, itemsContent, 'titleGroupId')
  const itemsList = fromJS(itemsOrdered.slice(0, MAX_ROW_ITEMS)) || List()

  return (
    <div className={`share-page__media-showcase${backgroundClass}`}>
      <H2 className="share-page__title share-page__media-showcase-title">{title}</H2>
      {text && <div className="share-page__media-showcase-text">{text}</div>}
      <SharePageSlider
        items={itemsList}
        speed={80}
        loading={loading}
        autoplay={autoplay}
      />
      { hasPeople && (
        <SharePageExperts
          sectionId={sectionId}
          pmSection={pmSection}
          language={language}
        />
      ) }
      { ctaLabel && offerLink && (
        <div className="share-page__media-showcase-cta-container">
          <Button
            className="share-page__media-showcase-cta"
            type={BUTTON_TYPES.PRIMARY}
            onClick={onOfferClick}
            {...offerLink}
          >
            {ctaLabel}
          </Button>
        </div>
      )}
    </div>
  )
}

SharePageMediaShowcase.defaultProps = {
  autoplay: false,
}

SharePageMediaShowcase.propTypes = {
  sectionData: ImmutablePropTypes.map.isRequired,
}

export default SharePageMediaShowcase
